/**
 * EPIC-DM01: Demo Mode Status Chip
 *
 * Pill-shaped status indicator with icon + text.
 * The "processing" variant includes an animated pulse.
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/* ------------------------------------------------------------------ */
/*  Variants                                                          */
/* ------------------------------------------------------------------ */

const demoChipVariants = cva(
  [
    'inline-flex items-center gap-2 rounded-full px-4 py-1.5',
    'text-demo-base font-semibold leading-tight whitespace-nowrap',
  ].join(' '),
  {
    variants: {
      status: {
        idle: 'bg-gray-100 text-demo-text-muted',
        processing: 'bg-demo-gold-light text-demo-gold-darkest animate-pulse',
        complete: 'bg-demo-green-light text-demo-green',
        error: 'bg-demo-error-light text-demo-error',
      },
    },
    defaultVariants: {
      status: 'idle',
    },
  },
);

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

export interface DemoChipProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof demoChipVariants> {
  /** Optional icon rendered before the label */
  icon?: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

const DemoChip = React.forwardRef<HTMLSpanElement, DemoChipProps>(
  ({ className, status, icon, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(demoChipVariants({ status }), className)}
        {...props}
      >
        {icon && <span className="flex-shrink-0" aria-hidden="true">{icon}</span>}
        {children}
      </span>
    );
  },
);

DemoChip.displayName = 'DemoChip';

export { DemoChip, demoChipVariants };
